import * as Sentry from "@sentry/react";
import { useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "@ndd/ui";
import { cn } from "@/lib/utils";
import type { CopyFormat } from "@/lib/analysis-copy-format";

interface CopyFormattedButtonProps {
  format: CopyFormat;
  children: ReactNode;
  className?: string;
}

async function writeFormattedClipboard(format: CopyFormat): Promise<void> {
  if (navigator.clipboard.write && typeof ClipboardItem !== "undefined") {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([format.html], { type: "text/html" }),
        "text/plain": new Blob([format.text], { type: "text/plain" }),
      }),
    ]);

    return;
  }

  await navigator.clipboard.writeText(format.text);
}

export function CopyFormattedButton({
  format,
  children,
  className,
}: CopyFormattedButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    try {
      await writeFormattedClipboard(format);
      setCopied(true);
      toast.success("Copied formatted text");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: "analysis_copy_formatted",
          source: "client",
          surface: "clipboard",
        },
      });
      toast.error("Could not copy text");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "inline-flex items-center justify-center gap-1 rounded-sm text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        copied && "text-success hover:text-success",
        className
      )}
    >
      {copied ? (
        <Check className="h-3 w-3 text-success" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      {children}
    </button>
  );
}
